import { useCallback, useState } from 'react';
import { toApiFileUrl } from '../../utils/s3Utils';

/**
 * What each uploaded proof image looks like, taken from the file that was
 * picked in the browser.
 *
 * The modal keeps the URLs the backend stored, because those are what gets
 * saved and what deleting takes, but it shows the local file. That is on screen
 * as soon as the upload answers and does not depend on where the backend keeps
 * the image: in development it is a relative path on the API's disk, which the
 * browser resolves against the front and never finds.
 */
export const useProofPreviews = () => {
  const [previews, setPreviews] = useState({});

  /** Pairs stored URLs with the files they came from, by position. */
  const remember = useCallback((urls, files) => {
    if (urls.length !== files.length) return;
    const added = {};
    urls.forEach((url, index) => {
      added[url] = URL.createObjectURL(files[index]);
    });
    setPreviews((prev) => ({ ...prev, ...added }));
  }, []);

  const forget = useCallback(
    (url) => {
      if (previews[url]) URL.revokeObjectURL(previews[url]);
      setPreviews((prev) => {
        const { [url]: _removed, ...rest } = prev;
        return rest;
      });
    },
    [previews]
  );

  const forgetAll = useCallback(() => {
    Object.values(previews).forEach((preview) => URL.revokeObjectURL(preview));
    setPreviews({});
  }, [previews]);

  /** The local file when there is one, the stored URL otherwise. */
  const previewOf = useCallback((url) => previews[url] || toApiFileUrl(url), [previews]);

  return { previewOf, remember, forget, forgetAll };
};
